import React from 'react';
import { CalendarClock, Settings } from 'lucide-react';

type OperatingHourConfig = {
  open: string;
  close: string;
  closed: boolean;
};

type ReservationsForm = {
  hasReservations: boolean;
  reservationSlotMinutes: string;
  maxReservationsPerSlot: string;
  maxGuestsPerSlot: string;
  operatingHours: Record<string, OperatingHourConfig>;
};

type ReservationsSettingsSectionProps = {
  reservationModuleEnabled: boolean;
  form: ReservationsForm;
  updateBooleanField: (field: 'hasReservations', value: boolean) => void;
  updateField: (field: string, value: string) => void;
  updateOperatingHour: (day: string, field: 'open' | 'close' | 'closed', value: string | boolean) => void;
};

const weekdayLabels = [
  { key: 'monday', label: 'Segunda' },
  { key: 'tuesday', label: 'Terca' },
  { key: 'wednesday', label: 'Quarta' },
  { key: 'thursday', label: 'Quinta' },
  { key: 'friday', label: 'Sexta' },
  { key: 'saturday', label: 'Sabado' },
  { key: 'sunday', label: 'Domingo' },
];

const ReservationsSettingsSection: React.FC<ReservationsSettingsSectionProps> = ({
  reservationModuleEnabled,
  form,
  updateBooleanField,
  updateField,
  updateOperatingHour,
}) => {
  const openDays = weekdayLabels.filter((day) => !form.operatingHours[day.key]?.closed).length;

  if (!reservationModuleEnabled) {
    return (
      <div id="secao-reservas" className="card scroll-mt-24 space-y-4 p-6">
        <div>
          <h2 className="text-lg font-bold text-white">Reservas e agenda</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            A plataforma desabilitou esse modulo para este restaurante. Isso e comum em operacoes focadas apenas em delivery ou retirada.
          </p>
        </div>
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
          Se este estabelecimento passar a atender em salao, reative o acesso a reservas no painel da plataforma.
        </div>
      </div>
    );
  }

  return (
    <div id="secao-reservas" className="card scroll-mt-24 space-y-6 p-6">
      <div className="grid gap-4 xl:grid-cols-[1.2fr,0.8fr] xl:items-start">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.28em] text-primary">
            <CalendarClock className="h-4 w-4" />
            Agenda
          </div>
          <h2 className="mt-2 text-xl font-bold text-white">Reservas e disponibilidade</h2>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            Defina quando o restaurante aceita reservas e como o sistema calcula disponibilidade por horario.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-1 2xl:grid-cols-3">
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
            <p className="mt-1 text-lg font-bold text-white">{form.hasReservations ? 'Ativas' : 'Pausadas'}</p>
          </div>
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Dias abertos</p>
            <p className="mt-1 text-lg font-bold text-white">{openDays} / 7</p>
          </div>
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Pessoas/slot</p>
            <p className="mt-1 text-lg font-bold text-white">{form.maxGuestsPerSlot || '0'}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[0.75fr,1.25fr]">
        <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
          <label className="text-sm font-medium text-white">Reservas de mesa</label>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
            <button
              type="button"
              onClick={() => updateBooleanField('hasReservations', true)}
              className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-all ${
                form.hasReservations
                  ? 'border-primary bg-primary/15 text-white'
                  : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
              }`}
            >
              Aceitar reservas
            </button>
            <button
              type="button"
              onClick={() => updateBooleanField('hasReservations', false)}
              className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-all ${
                !form.hasReservations
                  ? 'border-primary bg-primary/15 text-white'
                  : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
              }`}
            >
              Pausar reservas
            </button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Se estiver pausado, o botao de reservar nao aparece para o cliente e nenhuma nova reserva entra no sistema.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2 rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
            <label className="text-sm font-medium text-white">Intervalo por slot</label>
            <input
              type="number"
              min="5"
              step="5"
              value={form.reservationSlotMinutes}
              onChange={(e) => updateField('reservationSlotMinutes', e.target.value)}
              className="input"
            />
            <p className="text-xs text-muted-foreground">Cada reserva ocupa uma faixa de horario. Ex.: 30 minutos.</p>
          </div>
          <div className="space-y-2 rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
            <label className="text-sm font-medium text-white">Max. reservas por slot</label>
            <input
              type="number"
              min="1"
              value={form.maxReservationsPerSlot}
              onChange={(e) => updateField('maxReservationsPerSlot', e.target.value)}
              className="input"
            />
            <p className="text-xs text-muted-foreground">Limite extra de seguranca para a mesma faixa de horario.</p>
          </div>
          <div className="space-y-2 rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
            <label className="text-sm font-medium text-white">Max. pessoas por slot</label>
            <input
              type="number"
              min="1"
              value={form.maxGuestsPerSlot}
              onChange={(e) => updateField('maxGuestsPerSlot', e.target.value)}
              className="input"
            />
            <p className="text-xs text-muted-foreground">Limite total de pessoas aceitas no mesmo slot.</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-white">Horario de funcionamento para reservas</label>
          <p className="mt-1 text-xs text-muted-foreground">
            O cliente so consegue reservar dentro dos horarios liberados aqui. Se o dia estiver marcado como fechado, o sistema bloqueia reservas naquele dia.
          </p>
        </div>
        <div className="grid gap-3 2xl:grid-cols-2">
          {weekdayLabels.map((day) => {
            const config = form.operatingHours[day.key];
            return (
              <div
                key={day.key}
                className="grid gap-3 rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4 md:grid-cols-[140px,1fr,1fr,auto] md:items-center"
              >
                <div>
                  <p className="font-semibold text-white">{day.label}</p>
                  <p className="text-xs text-muted-foreground">Recebe reservas nesse dia.</p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">Abre</label>
                  <input
                    type="time"
                    value={config.open}
                    disabled={config.closed}
                    onChange={(e) => updateOperatingHour(day.key, 'open', e.target.value)}
                    className="input"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">Fecha</label>
                  <input
                    type="time"
                    value={config.close}
                    disabled={config.closed}
                    onChange={(e) => updateOperatingHour(day.key, 'close', e.target.value)}
                    className="input"
                  />
                </div>
                <label className="flex items-center gap-2 rounded-xl border border-[#2A2A2A] px-3 py-3 text-sm text-white">
                  <input
                    type="checkbox"
                    checked={config.closed}
                    onChange={(e) => updateOperatingHour(day.key, 'closed', e.target.checked)}
                    className="h-4 w-4 accent-orange-500"
                  />
                  Fechado
                </label>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-2xl border border-[#2A2A2A] bg-[#1A1A1A]/60 p-4 text-sm text-muted-foreground">
        <Settings className="mt-0.5 h-5 w-5 text-primary" />
        O sistema cruza horarios, mesas ativas e limites por slot para decidir se ainda existe disponibilidade real para uma nova reserva.
      </div>
    </div>
  );
};

export default ReservationsSettingsSection;
